import React, { Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeContext";
import { ImpersonationProvider, useImpersonation } from "./context/ImpersonationContext";
import Header from "./components/navigation/Header";
import PageLoader from "./components/common/PageLoader";
import ErrorBoundary from "./components/ErrorBoundary";
import LoginPage from "./auth/pages/LoginPage/LoginPage";
import { pages } from "./utils/page-permissions";
import NotFound from "./pages/NotFound/NotFound";
import Unauthorized from "./pages/Unauthorized/Unauthorized";
import useSessionTimeout from "./hooks/useSessionTimeout";
import { AnalyticsProvider } from "./context/AnalyticsProvider";
import { buildLoginPath } from "./utils/auth";

// Check if user is authenticated (has valid token and role)
function isAuthenticated() {
  const token = localStorage.getItem("authToken");
  const role = localStorage.getItem("userRole");
  return !!(token && role);
}

// Role-based home page routing
function getHomePageForRole(role) {
  const normalizedRole = role?.toLowerCase();

  switch (normalizedRole) {
    case 'hr':
    case 'hr_director':
      return '/dashboard';
    case 'ta':
      return '/ta';
    case 'ld':
    case 'ld_director':
      return '/ld';
    case 'rma':
      return '/rm/crm/tickets';
    case 'it':
      return '/it/crm/tickets';
    case 'admin':
    case 'mum':
    case 'rm':
    case 'director':
    case 'vp':
    default:
      return '/dashboard';
  }
}

// Redirects authenticated users to their role-specific home page
function HomeRedirect({ userRole, isLoading }) {
  if (isLoading) return <PageLoader />;

  if (!isAuthenticated()) {
    return <LoginPage />;
  }

  const homePage = getHomePageForRole(userRole);
  return <Navigate to={homePage} replace />;
}

function ProtectedRoute({ component: Component, componentProps, allowed, allowedEmails, userRole, isLoading }) {
  // Access impersonation context — effectiveRole overrides userRole when active
  const { effectiveRole } = useImpersonation();
  const activeRole = effectiveRole || userRole;
  const location = useLocation();

  // Show loader while checking auth status
  if (isLoading) return <PageLoader />;

  // Not authenticated - redirect to login, preserving the intended URL (+query)
  // as ?returnUrl= so a deep link (e.g. an email button) survives the round-trip.
  if (!isAuthenticated()) {
    return <Navigate to={buildLoginPath(location.pathname + location.search)} replace />;
  }

  // No role - redirect to login
  if (!activeRole) return <Navigate to="/" replace />;

  // Convert role to lowercase for comparison since allowed roles are lowercase
  const normalizedRole = activeRole.toLowerCase();

  // User doesn't have permission - redirect to their role's home page
  if (!allowed.includes(normalizedRole)) {
    const homePage = getHomePageForRole(activeRole);
    return <Navigate to={homePage} replace />;
  }

  // Email-restricted routes: check user email against allowedEmails whitelist
  // Skip email check during impersonation — the impersonated user's email
  // won't match but the real user (lbarbosa) should still be able to view
  if (allowedEmails && !effectiveRole) {
    const userEmail = (localStorage.getItem("userEmail") || "").toLowerCase();
    if (!allowedEmails.some(e => e.toLowerCase() === userEmail)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Render the lazy component with optional extra props
  return <Component {...componentProps} />;
}


// Session timeout configuration (in minutes)
const SESSION_TIMEOUT_MINUTES = 30;
const SESSION_WARNING_MINUTES = 5;

function App() {
  const location = useLocation();
  const [userRole, setUserRole] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Session timeout - auto-logout after 30 minutes of inactivity
  // Shows warning 5 minutes before logout
  useSessionTimeout(SESSION_TIMEOUT_MINUTES, SESSION_WARNING_MINUTES);

  // Update userRole when location changes (after login redirect)
  useEffect(() => {
    const role = localStorage.getItem("userRole") || '';
    console.log("App.js - userRole from localStorage:", role);
    setUserRole(role);
    setIsLoading(false);
  }, [location.pathname]);

  useEffect(() => {
    // ResizeObserver error handler
    const resizeObserverErrorHandler = (e) => {
      const errorMessages = [
        'ResizeObserver loop completed with undelivered notifications.',
        'ResizeObserver loop limit exceeded'
      ];

      if (errorMessages.some(msg => e.message?.includes(msg))) {
        e.stopImmediatePropagation();
        e.preventDefault();

        // Hide webpack dev server error overlay
        requestAnimationFrame(() => {
          const overlay = document.getElementById('webpack-dev-server-client-overlay');
          const overlayDiv = document.getElementById('webpack-dev-server-client-overlay-div');
          if (overlay) overlay.style.display = 'none';
          if (overlayDiv) overlayDiv.style.display = 'none';
        });
      }
    };

    // Attach error handler
    window.addEventListener('error', resizeObserverErrorHandler);

    // Clean console for staging/production
    const originalError = console.error;
    console.error = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('ResizeObserver loop')) {
        return;
      }
      originalError.apply(console, args);
    };

    // Cleanup
    return () => {
      window.removeEventListener('error', resizeObserverErrorHandler);
      console.error = originalError;
    };
  }, []);

  return (
    <ThemeProvider>
      <ImpersonationProvider>
      <AnalyticsProvider>
      <div className="App">
        <Header />
        <main className="app-content">
          <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Authentication Routes - these stay static, not lazy */}
              <Route path="/" element={<HomeRedirect userRole={userRole} isLoading={isLoading} />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/unauthorized" element={<Unauthorized />} />

              {/* Dynamic routes from page-permissions.js */}
              {pages.map((page) =>
                page.children ? (
                  <React.Fragment key={page.name}>
                    {page.children.map((child) => (
                      <Route
                        key={child.path}
                        path={child.path}
                        element={
                          <ProtectedRoute
                            component={child.component}
                            componentProps={child.componentProps}
                            allowed={child.allowed}
                            allowedEmails={child.allowedEmails}
                            userRole={userRole}
                            isLoading={isLoading}
                          />
                        }
                      />
                    ))}
                  </React.Fragment>
                ) : page.type !== 'external' ? (
                  <Route
                    key={page.path}
                    path={page.path}
                    element={
                      <ProtectedRoute
                        component={page.component}
                        componentProps={page.componentProps}
                        allowed={page.allowed}
                        allowedEmails={page.allowedEmails}
                        userRole={userRole}
                        isLoading={isLoading}
                      />
                    }
                  />
                ) : null
              )}

              {/* 404 Catch-all Route - must be last */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </main>
      </div>
      </AnalyticsProvider>
      </ImpersonationProvider>
    </ThemeProvider>
  );
}

export default App;
